import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getCertificateTemplates,
  createCertificateTemplate,
  updateCertificateTemplate,
  deleteCertificateTemplate,
} from '../../../services/trainingService';

const useCertificateTemplateApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    getCertificateTemplates(paramsToUse)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    try { await createCertificateTemplate(formData); setRefreshTrigger((p) => p + 1); return true; }
    catch { return false; }
  }, []);

  const update = useCallback(async (id, formData) => {
    try { await updateCertificateTemplate(id, formData); setRefreshTrigger((p) => p + 1); return true; }
    catch { return false; }
  }, []);

  const _delete = useCallback(async (ids) => {
    try { for (const id of ids) await deleteCertificateTemplate(id); setRefreshTrigger((p) => p + 1); return true; }
    catch { return false; }
  }, []);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('certificateTemplate.name'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'signerName', label: t('certificateTemplate.signerName'), options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' }, customBodyRender: (v) => v || '—' } },
    { name: 'signerTitle', label: t('certificateTemplate.signerTitle'), options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' }, customBodyRender: (v) => v || '—' } },
    { name: 'primaryColor', label: t('certificateTemplate.primaryColor'), options: { filter: false, sort: false, customBodyRender: (v) => v ? (
      <span>
        <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: v, verticalAlign: 'middle', marginRight: 6, border: '1px solid #E2E8F0' }} />
        {v}
      </span>
    ) : '—' } },
  ], [t]);

  const objectSchema = useMemo(() => [
    { name: 'name', label: t('certificateTemplate.name'), type: 'text', grid: 12, required: true },
    { name: 'logoUrl', label: t('certificateTemplate.logoUrl'), type: 'pictureUpload', grid: 6, accept: 'image/*', purpose: 'certificateTemplateAsset' },
    { name: 'backgroundUrl', label: t('certificateTemplate.backgroundUrl'), type: 'pictureUpload', grid: 6, accept: 'image/*', purpose: 'certificateTemplateAsset' },
    { name: 'signatureUrl', label: t('certificateTemplate.signatureUrl'), type: 'pictureUpload', grid: 6, accept: 'image/*', purpose: 'certificateTemplateAsset' },
    { name: 'primaryColor', label: t('certificateTemplate.primaryColor'), type: 'colorPicker', grid: 6 },
    { name: 'signerName', label: t('certificateTemplate.signerName'), type: 'text', grid: 6 },
    { name: 'signerTitle', label: t('certificateTemplate.signerTitle'), type: 'text', grid: 6 },
    { name: 'bodyText', label: t('certificateTemplate.bodyText'), type: 'textarea', grid: 12, required: true, placeholder: t('certificateTemplate.bodyTextHint') },
  ], [t]);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete };
};

export default useCertificateTemplateApi;
